import { useNotifications } from '@risksmart-app/components/src/notifications/useNotifications';
import type { ControlGroupsByTitleResponseRow } from '@risksmart-app/trpc/src/types';
import type { GetControlGroupsByTitleQuery } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { mapTRPCRefetch } from 'src/utils';
import { useTRPC } from 'src/utils/trpc';

import { useIsFeatureVisibleToOrg } from '@/utils/featureFlags';

/**
 * Maps TRPC control data to match the GraphQL query structure
 */
const mapTrpcControlGroupsToGraphQL = (
  trpcData: ControlGroupsByTitleResponseRow[]
): GetControlGroupsByTitleQuery => {
  return {
    control_group: trpcData,
  };
};

export const useGetControlGroupsByTitle = (title: string, skip?: boolean) => {
  const trpcEnabled = useIsFeatureVisibleToOrg('trpc');
  const trpc = useTRPC();
  const { addNotification } = useNotifications();
  const {
    data: trpcData,
    isLoading: trpcLoading,
    refetch: trpcRefetch,
    error: trpcError,
  } = useQuery({
    ...trpc.frontend.controlGroup.controlGroupsByTitle.queryOptions({
      title: title,
    }),
    enabled: trpcEnabled && !skip, // Only enable TRPC query when flag is true
  });

  // Handle TRPC errors
  useEffect(() => {
    if (trpcEnabled && trpcError) {
      addNotification({
        type: 'error',
        content: <>{trpcError.message}</>,
      });
    }
  }, [trpcEnabled, trpcError, addNotification]);

  // Transform TRPC data to match the same structure as GraphQL query
  const data = trpcData ? mapTrpcControlGroupsToGraphQL(trpcData) : undefined;

  return {
    loading: trpcLoading,
    data,
    refetch: () => mapTRPCRefetch(trpcRefetch, mapTrpcControlGroupsToGraphQL),
    error: trpcError,
  };
};
